import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { StatusSelector } from '@klp/design/StatusSelector/StatusSelector';
import { RegistrationStatuses } from '../services/rtkApi';
import { contractToRegistrationSelectors, setStatus } from '../store';
import {
    getRegistreringStatusTranslations,
    useRegistrationRoles,
} from '../utils';

export const AllRegistrationStatuses = [
    'IkkeAvsjekket',
    'Avsjekket',
    RegistrationStatuses.NY,
    RegistrationStatuses.SKAL_IKKE_REGISTRERES,
    RegistrationStatuses.GARANTI_REGISTRERING,
    RegistrationStatuses.HOS_FORVALTER,
    RegistrationStatuses.TILDELT_OKONOMI,
    RegistrationStatuses.REGISTRERING_D365,
    RegistrationStatuses.TIL_QA,
    RegistrationStatuses.QA_UTFORT,
    RegistrationStatuses.TIL_GODKJENNING,
    RegistrationStatuses.GODKJENT,
    RegistrationStatuses.FULLFORT,
];

interface RegistreringStatusSelectorProps {
    handleApplyFilter: (selected: Array<string>) => void;
    disabled?: boolean;
}

export default function RegistreringStatusSelector({
    handleApplyFilter,
    disabled,
}: RegistreringStatusSelectorProps) {
    const dispatch = useDispatch();

    const { registrationStatus: defaultStatusForRole } = useRegistrationRoles();
    const { status: statusFromStorage } = useSelector(
        contractToRegistrationSelectors,
    );
    const validStorageStatuses = useMemo(() => {
        return statusFromStorage.filter(
            (s: string) =>
                typeof s === 'string' && AllRegistrationStatuses.includes(s),
        );
    }, [statusFromStorage]);

    const [selectedStatus, setSelectedStatus] = useState<Array<string>>();

    useEffect(() => {
        // Handle initial load and use default if localstorage is empty
        if (!selectedStatus && validStorageStatuses.length === 0) {
            setSelectedStatus(defaultStatusForRole);
            handleApplyFilter(defaultStatusForRole);
            return;
        }

        setSelectedStatus(validStorageStatuses);
        handleApplyFilter(validStorageStatuses);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [validStorageStatuses]);

    const handleStatusChange = (statuses: Array<string>) => {
        setSelectedStatus(statuses);
        dispatch(setStatus(statuses));
        handleApplyFilter(statuses);
    };

    return (
        <StatusSelector
            label="filterPanel.status"
            allLabel="filterPanel.alle"
            statusList={AllRegistrationStatuses}
            statusListIsLoading={!selectedStatus}
            value={selectedStatus ?? []}
            onChange={handleStatusChange}
            getStatusLabel={getRegistreringStatusTranslations}
            displayMode="summary"
            disabled={disabled}
        />
    );
}
